import React, { useEffect } from 'react';
import { Row, Col, FormGroup } from 'reactstrap';
import { Field, Input } from '@availity/form';
import { useFormikContext } from 'formik';
import { PracticeLocationData } from '../../../../UserPages/Login/PracticeRegistration/Model/PracticeLocationData';
import StateSelectField from '../../../../../components/FormFields/StateSelectField/StateSelectField';
import './PmgMembershipProfileLocations.scss';
import {
    PracticeFocus,
    PracticeFocusOptionsMapping,
} from '../../../../../utils/constants/PracticeFocus.enum';

interface PmgMembershipProfileLocationsProps {
    practiceLocationData: PracticeLocationData[];
    readOnlyView?: boolean;
}

const PmgMembershipProfileLocations: React.FC<
    PmgMembershipProfileLocationsProps
> = ({ practiceLocationData, readOnlyView = false }) => {
    const { values } = useFormikContext<any>();

    if (!practiceLocationData || practiceLocationData.length === 0) {
        return null;
    }

    const isOtherFocusFieldVisible = (index: number) => {
        return (
            values?.practiceLocationData?.[index]?.practiceFocus.toString() ===
                PracticeFocus.Other.toString() && !readOnlyView
        );
    };

    const renderOtherFocusField = (index: number) => {
        return (
            <Col md={6}>
                {readOnlyView ? (
                    <div className="flex-column-container">
                        <label>Please specify the service focus</label>
                        {getPracticeLocationFocusLabel(index)}
                    </div>
                ) : (
                    <FormGroup>
                        <label>Please specify the service focus</label>
                        <Field
                            type="text"
                            name={`practiceLocationData[${index}].otherPracticeType`}
                            placeholder="Enter service focus type"
                            disabled={readOnlyView}
                        />
                    </FormGroup>
                )}
            </Col>
        );
    };

    const getPracticeLocationFocusLabel = (index: number) => {
        if (
            values.practiceLocationData[index].practiceFocus ===
            PracticeFocus.Other
        ) {
            return (
                values.practiceLocationData[index].otherPracticeType ||
                'Other (not specified)'
            );
        } else {
            return (
                PracticeFocusOptionsMapping.find(
                    (item) =>
                        item.id ===
                        values.practiceLocationData[index].practiceFocus
                )?.label || 'N/A'
            );
        }
    };

    return (
        <div className="practice-locations-container">
            {practiceLocationData.map((location, index) => (
                <div key={index} className="practice-location-item">
                    {readOnlyView ? (
                        <div className="location-name-header">
                            {location.practiceLocationName ||
                                `Location ${index + 1}`}
                        </div>
                    ) : (
                        <Row>
                            <Col lg={12}>
                                <FormGroup>
                                    <Field
                                        type="text"
                                        label="Location Name"
                                        name={`practiceLocationData[${index}].practiceLocationName`}
                                        disabled={readOnlyView}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                    <Row className="address-area">
                        <Col lg={12}>
                            {readOnlyView ? (
                                <div className="dynamic_data">
                                    <label>Street Address</label>
                                    <span className="profile_data">
                                        {location.address || 'N/A'}
                                    </span>
                                </div>
                            ) : (
                                <FormGroup>
                                    <Field
                                        type="text"
                                        label="Street Address"
                                        name={`practiceLocationData[${index}].address`}
                                        disabled={readOnlyView}
                                    />
                                </FormGroup>
                            )}
                        </Col>
                        <Col lg={4}>
                            {readOnlyView ? (
                                <div className="dynamic_data">
                                    <label>City</label>
                                    <span className="profile_data">
                                        {location.city || 'N/A'}
                                    </span>
                                </div>
                            ) : (
                                <FormGroup>
                                    <Field
                                        type="text"
                                        label="City"
                                        name={`practiceLocationData[${index}].city`}
                                        disabled={readOnlyView}
                                    />
                                </FormGroup>
                            )}
                        </Col>
                        <Col lg={4}>
                            {readOnlyView ? (
                                <div className="dynamic_data">
                                    <label>State</label>
                                    <span className="profile_data">
                                        {location.state || 'N/A'}
                                    </span>
                                </div>
                            ) : (
                                <StateSelectField
                                    name={`practiceLocationData[${index}].state`}
                                    disabled={readOnlyView}
                                    label="State"
                                />
                            )}
                        </Col>
                        <Col lg={4}>
                            {readOnlyView ? (
                                <div className="dynamic_data">
                                    <label>Zip Code</label>
                                    <span className="profile_data">
                                        {location.zipCode || 'N/A'}
                                    </span>
                                </div>
                            ) : (
                                <FormGroup>
                                    <Field
                                        type="text"
                                        label="Zip Code"
                                        name={`practiceLocationData[${index}].zipCode`}
                                        disabled={readOnlyView}
                                        maxLength={5}
                                        minLength={5}
                                        pattern="[0-9]{5}"
                                    />
                                </FormGroup>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={6}>
                            {readOnlyView ? (
                                <div className="dynamic_data">
                                    <label>Service Focus</label>
                                    <span className="profile_data">
                                        {getPracticeLocationFocusLabel(index)}
                                    </span>
                                </div>
                            ) : (
                                <FormGroup>
                                    <label>Service Focus</label>
                                    <Input
                                        type="select"
                                        name={`practiceLocationData[${index}].practiceFocus`}
                                        disabled={readOnlyView}>
                                        {[
                                            { id: '', label: 'Select' },
                                            ...PracticeFocusOptionsMapping,
                                        ].map((item) => (
                                            <option
                                                value={item.id}
                                                key={`practiceFocus${item.id}`}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            )}
                        </Col>
                        {isOtherFocusFieldVisible(index) ? (
                            renderOtherFocusField(index)
                        ) : (
                            <></>
                        )}
                    </Row>
                </div>
            ))}
        </div>
    );
};

export default PmgMembershipProfileLocations;
